import weightTable from './who/weight.json';
import lengthHeightTable from './who/length_height.json';
import headTable from './who/head.json';
import bmiTable from './who/bmi.json';
import { lmsAt, percentileFromZ, valueAtZ, zScore } from './growthLms';

/**
 * Indicadores da curva de crescimento sobre as tabelas da OMS: idade na
 * medição, escolha da tabela (semanas, comprimento ou altura), escore-z,
 * percentil, curvas de referência e faixas de validação. Sem React.
 */

export const DAYS_PER_MONTH = 30.4375;
const WEEKS_LIMIT_DAYS = 13 * 7;
const LENGTH_LIMIT_MONTHS = 24;
/** Diferença padrão da OMS entre comprimento (deitado) e altura (em pé). */
const LYING_STANDING_DIFF = 0.7;

export const GROWTH_INDICATORS = [
    {
        key: 'weight', label: 'Peso/idade', short: 'Peso', unit: 'kg', digits: 2, table: weightTable,
        restricted: true, maxMonths: 120, implausible: [-6, 5],
        windows: [[0, 24], [0, 60], [60, 120], [0, 120]],
        beyond: 'A OMS publica peso/idade só até 10 anos. Acompanhe pelo IMC/idade.',
    },
    {
        key: 'height', label: 'Comprimento/altura/idade', short: 'Comp./altura', unit: 'cm', digits: 1, table: lengthHeightTable,
        restricted: false, maxMonths: 228, implausible: [-6, 6], split: true,
        windows: [[0, 24], [0, 60], [60, 228], [0, 228]],
        beyond: 'As curvas da OMS vão até 19 anos.',
    },
    {
        key: 'head', label: 'Perímetro cefálico/idade', short: 'PC', unit: 'cm', digits: 1, table: headTable,
        restricted: false, maxMonths: 60, implausible: [-5, 5],
        windows: [[0, 24], [0, 60]],
        beyond: 'A OMS publica perímetro cefálico/idade só até 5 anos.',
    },
    {
        key: 'bmi', label: 'IMC/idade', short: 'IMC', unit: 'kg/m²', digits: 1, table: bmiTable,
        restricted: true, maxMonths: 228, implausible: [-5, 5], split: true,
        windows: [[0, 24], [0, 60], [60, 228], [0, 228]],
        beyond: 'As curvas da OMS vão até 19 anos.',
    },
];

export const getIndicator = (key) => GROWTH_INDICATORS.find((indicator) => indicator.key === key);

/** Faixas absolutas aceitas no cadastro (as mesmas do PHP). */
export const GROWTH_LIMITS = {
    weight: { min: 0.3, max: 250, label: 'Peso', unit: 'kg' },
    height: { min: 25, max: 250, label: 'Comprimento/altura', unit: 'cm' },
    headCircumference: { min: 20, max: 70, label: 'Perímetro cefálico', unit: 'cm' },
    bmi: { min: 5, max: 100, label: 'IMC', unit: 'kg/m²' },
};

export const WHO_ATTRIBUTION = 'Referência: OMS, WHO Child Growth Standards (2006) e WHO Growth Reference 2007.';

/* ---------- datas, idade e sexo ---------- */

/** 'YYYY-MM-DD', 'YYYY-MM-DD HH:mm:ss' ou 'DD/MM/YYYY' em data UTC; inválida = null. */
export const parseDate = (value) => {
    if (!value) return null;
    const text = String(value).trim();
    let match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
    let parts = match ? [match[1], match[2], match[3]] : null;
    if (!parts) {
        match = text.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
        parts = match ? [match[3], match[2], match[1]] : null;
    }
    if (!parts) return null;
    const [year, month, day] = parts.map(Number);
    if (!year || !month || !day) return null;
    return Date.UTC(year, month - 1, day);
};

export const ageInDays = (birthday, date) => {
    const birth = parseDate(birthday);
    const at = parseDate(date);
    if (birth == null || at == null) return null;
    return Math.round((at - birth) / 86400000);
};

export const sexFromGender = (gender) => {
    if (gender === '0' || gender === 0) return 'male';
    if (gender === '1' || gender === 1) return 'female';
    return null;
};

/** O que falta no cadastro do paciente para calcular a curva. */
export const missingPatientInfo = (birthday, gender) => {
    const missing = [];
    if (parseDate(birthday) == null) missing.push('data de nascimento');
    if (!sexFromGender(gender)) missing.push('sexo (masculino ou feminino)');
    return missing;
};

export const formatAge = (days) => {
    if (days == null || days < 0) return '-';
    if (days < 31) return days === 1 ? '1 dia' : `${days} dias`;
    const months = Math.floor(days / DAYS_PER_MONTH);
    if (months < 24) return months === 1 ? '1 mês' : `${months} meses`;
    const years = Math.floor(months / 12);
    const rest = months % 12;
    if (!rest) return `${years} anos`;
    return `${years} anos e ${rest === 1 ? '1 mês' : `${rest} meses`}`;
};

/* ---------- valores da medição ---------- */

const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(String(value).replace(',', '.'));
    return Number.isFinite(number) ? number : null;
};

/**
 * Comprimento/altura comparável com a tabela da idade: abaixo de 24 meses a
 * OMS usa comprimento (deitado), a partir de 24 meses altura (em pé). Medido
 * na outra posição, soma ou subtrai 0,7 cm, como a OMS orienta.
 */
export const adjustedHeight = (height, position, ageMonths) => {
    if (height == null) return null;
    if (ageMonths == null) return height;
    if (ageMonths < LENGTH_LIMIT_MONTHS && position === 'standing') return height + LYING_STANDING_DIFF;
    if (ageMonths >= LENGTH_LIMIT_MONTHS && position === 'lying') return height - LYING_STANDING_DIFF;
    return height;
};

export const computeBmi = (weight, height) => {
    if (!(weight > 0) || !(height > 0)) return null;
    return weight / Math.pow(height / 100, 2);
};

/** Mensagem de erro do formulário, ou null se a medição é aceitável. */
export const validateMeasurement = ({ measuredAt, weight, height, headCircumference }, birthday) => {
    const values = { weight: toNumber(weight), height: toNumber(height), headCircumference: toNumber(headCircumference) };
    if (!measuredAt || parseDate(measuredAt) == null) return 'Informe a data da medição.';
    const today = new Date();
    if (parseDate(measuredAt) > Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) {
        return 'A data da medição não pode estar no futuro.';
    }
    const birth = parseDate(birthday);
    if (birth != null && parseDate(measuredAt) < birth) return 'A data da medição é anterior ao nascimento do paciente.';
    if (Object.values(values).every((value) => value == null)) {
        return 'Informe ao menos peso, comprimento/altura ou perímetro cefálico.';
    }
    for (const [key, value] of Object.entries(values)) {
        const limit = GROWTH_LIMITS[key];
        if (value != null && (value < limit.min || value > limit.max)) {
            return `${limit.label} fora da faixa aceita (${limit.min} a ${limit.max} ${limit.unit}). Confira o valor e a unidade.`;
        }
    }
    const bmi = computeBmi(values.weight, values.height);
    if (bmi != null && (bmi < GROWTH_LIMITS.bmi.min || bmi > GROWTH_LIMITS.bmi.max)) {
        return `Peso e comprimento/altura resultam em IMC ${bmi.toFixed(1).replace('.', ',')}, fora do possível. Confira os valores.`;
    }
    return null;
};

/* ---------- tabela da OMS para uma idade ---------- */

/** L, M, S do indicador na idade (em dias), ou null fora da cobertura. */
export const referenceAt = (indicator, sex, days) => {
    const table = indicator.table[sex];
    if (!table || days == null || days < 0) return null;
    if (days <= WEEKS_LIMIT_DAYS) return lmsAt(table.weeks, days / 7);
    const months = days / DAYS_PER_MONTH;
    if (months > indicator.maxMonths) return null;
    if (!indicator.split) return lmsAt(table.months, months);
    return lmsAt(months < LENGTH_LIMIT_MONTHS ? table.length : table.height, months);
};

const measurementValues = (measurement, ageMonths) => {
    const weight = toNumber(measurement.weight);
    const rawHeight = toNumber(measurement.height);
    const height = adjustedHeight(rawHeight, measurement.heightPosition, ageMonths);
    return {
        display: { weight, height: rawHeight, head: toNumber(measurement.headCircumference), bmi: computeBmi(weight, rawHeight) },
        reference: { weight, height, head: toNumber(measurement.headCircumference), bmi: computeBmi(weight, height) },
    };
};

/**
 * Idade, valores e, por indicador, escore-z e percentil de uma medição.
 * `indicators[key].status`: 'ok', 'missing' (sem nascimento ou sexo) ou
 * 'out_of_range' (idade fora da tabela da OMS).
 */
export const measurementStats = (measurement, birthday, gender) => {
    const sex = sexFromGender(gender);
    const days = ageInDays(birthday, measurement.measuredAt);
    const ageMonths = days == null ? null : days / DAYS_PER_MONTH;
    const { display, reference } = measurementValues(measurement, ageMonths);
    const indicators = {};
    GROWTH_INDICATORS.forEach((indicator) => {
        const value = display[indicator.key];
        if (value == null) return;
        const base = { value, chartValue: reference[indicator.key] };
        if (!sex || days == null || days < 0) {
            indicators[indicator.key] = { ...base, status: 'missing' };
            return;
        }
        const lms = referenceAt(indicator, sex, days);
        if (!lms) {
            indicators[indicator.key] = { ...base, status: 'out_of_range' };
            return;
        }
        const z = zScore(reference[indicator.key], lms, indicator.restricted);
        const [low, high] = indicator.implausible;
        indicators[indicator.key] = {
            ...base, status: 'ok', z, percentile: percentileFromZ(z), implausible: z < low || z > high,
        };
    });
    return { measurement, days, ageMonths, ageLabel: formatAge(days), values: display, indicators };
};

/* ---------- curvas de referência ---------- */

const toMonths = (weeks) => (weeks * 7) / DAYS_PER_MONTH;

/** Trechos contínuos da tabela em meses; comprimento e altura são trechos separados (salto de 0,7 cm aos 24 meses). */
const tableSegments = (indicator, sex) => {
    const table = indicator.table[sex];
    const weeks = table.weeks.map(([w, L, M, S]) => [toMonths(w), L, M, S]);
    const firstMonths = indicator.split ? table.length : table.months;
    const early = weeks.concat(firstMonths.filter((row) => row[0] > weeks[weeks.length - 1][0]));
    return indicator.split ? [early, table.height] : [early];
};

/**
 * Curvas de referência dentro da janela [de, até] em meses.
 * Devolve trechos: `[{ lines: [{ label, z, points: [[meses, valor]] }] }]`.
 */
export const referenceCurves = (indicator, sex, [from, to], lines) => {
    if (!sex) return [];
    return tableSegments(indicator, sex)
        .map((rows) => rows.filter((row) => row[0] >= from && row[0] <= to))
        .filter((rows) => rows.length > 1)
        .map((rows) => ({
            lines: lines.map((line) => ({
                ...line,
                points: rows
                    .map(([x, L, M, S]) => [x, valueAtZ({ L, M, S }, line.z)])
                    .filter(([, y]) => y != null),
            })),
        }));
};

/** Menor janela do indicador que contém todas as idades (em meses) dentro da cobertura. */
export const chooseWindow = (indicator, ages) => {
    const covered = ages.filter((age) => age != null && age >= 0 && age <= indicator.maxMonths);
    if (!covered.length) return indicator.windows[0];
    const min = Math.min(...covered);
    const max = Math.max(...covered);
    return indicator.windows.find(([from, to]) => min >= from && max <= to) || indicator.windows[indicator.windows.length - 1];
};

/* ---------- formatação ---------- */

export const formatNumber = (value, digits = 1) => {
    if (value == null || !Number.isFinite(value)) return '-';
    return value.toLocaleString('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

export const formatZ = (z) => {
    if (z == null) return '-';
    const text = formatNumber(Math.abs(z), 2);
    if (Math.abs(z) < 0.005) return text;
    return z > 0 ? `+${text}` : `-${text}`;
};

export const formatPercentile = (percentile) => {
    if (percentile == null) return '-';
    if (percentile < 0.1) return '< 0,1';
    if (percentile > 99.9) return '> 99,9';
    return formatNumber(percentile, 1);
};
